/**
 * Centered success modal shown the moment `mobile-ticket/create` succeeds.
 *
 * Replaces the in-step success view (which was decent but cramped) with a
 * focused, hero-style confirmation that surfaces the ticket code first.
 * Renders via a React portal so it overlays everything regardless of where
 * the booking flow lives in the tree.
 *
 * The modal exposes a single primary CTA (View tickets when logged in,
 * otherwise New booking) plus a custom "Share via" row with first-class
 * targets: WhatsApp, Viber, Telegram, Instagram, Facebook, Gmail. We
 * deliberately avoid the Web Share API here — the native share sheet
 * looks foreign next to the rest of the brand and isn't available on
 * most desktops. Each chip opens the corresponding app's deep-link /
 * share URL with the ticket details pre-filled. Instagram has no public
 * web-share URL, so we copy the message to the clipboard and open
 * instagram.com instead.
 */

import { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { pickAddress, pickTranslation } from "../../helper/bookingApi";
import {
  dayKey,
  formatClockTime,
  formatDayLabel,
  parseSlotDate,
} from "./bookingFormat";
import { BOOKING_FONT_FAMILY } from "./BookingPrimitives";

const BACKDROP_BG = "rgba(15, 23, 42, 0.55)";

/**
 * Build the deep-link / share URL list for each first-class share target.
 *
 * - WhatsApp: `wa.me` works on mobile (opens app) and desktop (opens
 *   web.whatsapp.com after the user is signed in).
 * - Viber: `viber://forward` only works when the Viber app is installed,
 *   so on desktops without Viber the click is silently ignored — the
 *   user can fall back to one of the other targets.
 * - Telegram: web share URL works on every platform.
 * - Instagram: there's no public share URL for plain text. We copy the
 *   message to the clipboard and open instagram.com so the user can
 *   paste into a DM.
 * - Facebook: the public sharer accepts a `u` (URL) param. The `quote`
 *   param is ignored for non-page URLs but harmless to include.
 * - Gmail: open the Gmail compose window pre-populated with subject /
 *   body. Falls back to a `mailto:` URI on devices without Gmail web.
 */
function buildShareTargets({ text, url, subject, calendarUrl }) {
  const t = encodeURIComponent(text);
  const u = encodeURIComponent(url || "");
  const s = encodeURIComponent(subject || "");
  const tu = url ? `${t}%0A${u}` : t;
  return [
    {
      id: "whatsapp",
      label: "WhatsApp",
      bg: "#25D366",
      icon: "lab la-whatsapp",
      href: `https://wa.me/?text=${tu}`,
    },
    {
      id: "viber",
      label: "Viber",
      bg: "#7360F2",
      icon: "lab la-viber",
      href: `viber://forward?text=${tu}`,
    },
    {
      id: "telegram",
      label: "Telegram",
      bg: "#2AABEE",
      icon: "lab la-telegram-plane",
      href: `https://t.me/share/url?url=${u || t}&text=${t}`,
    },
    {
      id: "instagram",
      label: "Instagram",
      // Instagram has no plain-text web share URL. We copy + open IG.
      bgGradient:
        "radial-gradient(circle at 30% 110%, #fdf497 0%, #fdf497 5%, #fd5949 45%, #d6249f 60%, #285AEB 90%)",
      icon: "lab la-instagram",
      copyAndOpen: "https://www.instagram.com/",
    },
    {
      id: "facebook",
      label: "Facebook",
      bg: "#1877F2",
      icon: "lab la-facebook-f",
      href: `https://www.facebook.com/sharer/sharer.php?u=${u}&quote=${t}`,
    },
    {
      id: "google-calendar",
      label: "Calendar",
      bg: "#F4F5F7",
      iconColor: "#4285F4",
      bordered: true,
      icon: "las la-calendar-plus",
      href: calendarUrl,
    },
    {
      id: "gmail",
      label: "Gmail",
      // Match the screenshot: light circle with envelope outline, brand-
      // neutral so it sits next to the colored chips without clashing.
      bg: "#F4F5F7",
      iconColor: "#1f2937",
      bordered: true,
      icon: "las la-envelope",
      href: `https://mail.google.com/mail/?view=cm&fs=1&su=${s}&body=${tu}`,
      mailto: `mailto:?subject=${s}&body=${tu}`,
    },
  ].filter((target) => target.href || target.copyAndOpen);
}

function formatGoogleCalendarDate(date) {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return date.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}Z$/, "Z");
}

function getServiceDurationMinutes(service) {
  const raw =
    service?.average_waiting ??
    service?.duration ??
    service?.waiting_time ??
    service?.category?.average_waiting ??
    service?.meta?.average_waiting;
  const minutes = Number(raw);
  return Number.isFinite(minutes) && minutes > 0 ? minutes : 30;
}

function buildGoogleCalendarUrl({
  title,
  startDate,
  durationMinutes,
  details,
  location,
}) {
  if (!(startDate instanceof Date) || Number.isNaN(startDate.getTime())) return "";
  const endDate = new Date(startDate.getTime() + durationMinutes * 60 * 1000);
  const dates = `${formatGoogleCalendarDate(startDate)}/${formatGoogleCalendarDate(endDate)}`;
  const params = new URLSearchParams({
    action: "TEMPLATE",
    text: title || "Nomio booking",
    dates,
    details: details || "",
    location: location || "",
  });
  return `https://calendar.google.com/calendar/render?${params.toString()}`;
}

/**
 * Single circular share-target chip with a label underneath.
 *
 * Sized via `clamp()` so 6 chips fit a 320px-wide phone (~36px circle) and
 * scale up to a comfortable 52px on tablet+. The label is allowed to wrap
 * to two lines on narrow screens.
 */
function ShareChip({ target, palette, onClick }) {
  const iconStyle = {
    color: target.iconColor || "#fff",
    fontSize: "clamp(1.1rem, 4vw, 1.4rem)",
    lineHeight: 1,
  };
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={target.label}
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        cursor: "pointer",
        minWidth: 0,
        width: "100%",
      }}
    >
      <span
        style={{
          width: "clamp(36px, 11vw, 52px)",
          height: "clamp(36px, 11vw, 52px)",
          borderRadius: "50%",
          background: target.bgGradient || target.bg,
          border: target.bordered ? "1px solid #e5e7eb" : "none",
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0 4px 10px rgba(15, 23, 42, 0.08)",
          flexShrink: 0,
          transition: "transform 0.12s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.05)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
        }}
      >
        <i className={target.icon} aria-hidden="true" style={iconStyle} />
      </span>
      <span
        style={{
          // Single-word brand labels — let them wrap to a second line on
          // tiny phones rather than truncate with ellipsis. `wordBreak`
          // keeps full words intact (no "WhatsA…pp" or "Inst-agram"
          // hyphenation).
          fontSize: "clamp(0.62rem, 2vw, 0.78rem)",
          color: palette.textMuted,
          textAlign: "center",
          lineHeight: 1.15,
          maxWidth: "100%",
          wordBreak: "keep-all",
          overflowWrap: "anywhere",
        }}
      >
        {target.label}
      </span>
    </button>
  );
}

function buildMapUrl({ orgName, branchName, address }) {
  const query = [branchName || orgName, address].filter(Boolean).join(", ");
  if (!query) return "";
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`;
}

function buildShareText({
  t,
  code,
  orgName,
  branchName,
  dateLabel,
  timeLabel,
  address,
  mapUrl,
}) {
  const lines = [t("booking.successModal.share.heading", { code })];
  if (branchName) lines.push(`${t("booking.successModal.summary.branch")}: ${branchName}`);
  if (orgName && orgName !== branchName) {
    lines.push(`${t("booking.successModal.summary.organization")}: ${orgName}`);
  }
  if (dateLabel || timeLabel) {
    const dt = [dateLabel, timeLabel].filter(Boolean).join(", ");
    lines.push(`${t("booking.successModal.summary.datetime")}: ${dt}`);
  }
  if (address) {
    lines.push(`${t("booking.successModal.summary.address")}: ${address}`);
  }
  if (mapUrl) {
    lines.push(`${t("booking.successModal.summary.map")}: ${mapUrl}`);
  }
  return lines.join("\n");
}

export function TicketSuccessModal({
  isOpen,
  onClose,
  palette,
  lang,
  ticket,
  organization,
  branch,
  service,
  slot,
  isAuthenticated,
  ticketsHref,
  onNewBooking,
  onRegister,
}) {
  const { t } = useTranslation();
  const [shareState, setShareState] = useState("idle"); // idle | shared | copied | unsupported

  // Lock body scroll while open + handle Escape. Also reset transient
  // share feedback so reopening the modal always starts in the neutral
  // "Share" state.
  useEffect(() => {
    if (!isOpen) {
      setShareState("idle");
      return undefined;
    }
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, onClose]);

  const code = ticket?.code || "";
  const orgName = organization ? pickTranslation(organization, lang) : "";
  const branchName = branch ? pickTranslation(branch, lang) : "";
  const serviceName = service ? pickTranslation(service, lang) : "";
  const address = branch ? pickAddress(branch) : "";
  const mapUrl = useMemo(
    () => buildMapUrl({ orgName, branchName, address }),
    [orgName, branchName, address],
  );

  const slotDate = useMemo(() => {
    if (!slot) return null;
    if (slot.date instanceof Date) return slot.date;
    if (slot.raw) return parseSlotDate(slot.raw);
    return null;
  }, [slot]);

  // Compact numeric date for the in-modal pill (matches the screenshot
  // design: "2026-06-08"). Share payloads use the human-readable localized
  // form so messengers stay friendly.
  const dateLabel = slotDate ? dayKey(slotDate) : "";
  const timeLabel = slotDate ? formatClockTime(slotDate) : "";
  const friendlyDateLabel = slotDate ? formatDayLabel(slotDate, lang, t) : "";

  /** Best-effort copy that falls back through three increasingly-old APIs. */
  const copyText = useCallback((payload) => {
    try {
      if (navigator.clipboard?.writeText) {
        return navigator.clipboard.writeText(payload).then(
          () => true,
          () => false,
        );
      }
    } catch (_e) {
      /* fall through */
    }
    try {
      const el = document.createElement("textarea");
      el.value = payload;
      document.body.appendChild(el);
      el.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(el);
      return Promise.resolve(ok);
    } catch (_e) {
      return Promise.resolve(false);
    }
  }, []);

  const shareText = useMemo(
    () =>
      buildShareText({
        t,
        code,
        orgName,
        branchName,
        dateLabel: friendlyDateLabel || dateLabel,
        timeLabel,
        address,
        mapUrl,
      }),
    [t, code, orgName, branchName, friendlyDateLabel, dateLabel, timeLabel, address, mapUrl],
  );

  const calendarUrl = useMemo(
    () =>
      buildGoogleCalendarUrl({
        title: [serviceName, branchName || orgName].filter(Boolean).join(" - "),
        startDate: slotDate,
        durationMinutes: getServiceDurationMinutes(service),
        details: shareText,
        location: [branchName || orgName, address].filter(Boolean).join(", "),
      }),
    [address, branchName, orgName, service, serviceName, shareText, slotDate],
  );

  const shareUrl =
    typeof window !== "undefined"
      ? `${window.location.origin}${ticketsHref}`
      : "";

  const shareTargets = useMemo(
    () =>
      buildShareTargets({
        text: shareText,
        url: shareUrl,
        subject: t("booking.successModal.share.title"),
        calendarUrl,
      }),
    [calendarUrl, shareText, shareUrl, t],
  );

  const handleShareTarget = useCallback(
    async (target) => {
      // Instagram has no direct text-share URL; the standard pattern is to
      // copy the message and route the user into the Instagram web app so
      // they can paste into a DM or story.
      if (target.copyAndOpen) {
        const ok = await copyText(shareUrl ? `${shareText}\n${shareUrl}` : shareText);
        setShareState(ok ? "copied" : "unsupported");
        try {
          window.open(target.copyAndOpen, "_blank", "noopener,noreferrer");
        } catch (_e) {
          /* popup blocked — copy state already set as feedback */
        }
        return;
      }
      // Gmail: try the web compose URL first, fall back to a `mailto:` URI.
      // Most desktops have a default mail handler even when the user isn't
      // on Gmail, so the action never silently fails.
      try {
        if (target.id === "gmail" && target.mailto) {
          const win = window.open(
            target.href,
            "_blank",
            "noopener,noreferrer",
          );
          if (!win) window.location.href = target.mailto;
        } else if (target.href) {
          window.open(target.href, "_blank", "noopener,noreferrer");
        }
        setShareState("shared");
      } catch (_e) {
        setShareState("unsupported");
      }
    },
    [copyText, shareText, shareUrl],
  );

  // Auto-revert "copied" / "shared" feedback after 2.5s.
  useEffect(() => {
    if (shareState === "idle") return undefined;
    const id = setTimeout(() => setShareState("idle"), 2500);
    return () => clearTimeout(id);
  }, [shareState]);

  if (!isOpen) return null;

  // Compact toast-style feedback rendered next to the share heading.
  const shareFeedback =
    shareState === "copied"
      ? t("booking.successModal.share.copied")
      : shareState === "shared"
        ? t("booking.successModal.share.opened")
        : shareState === "unsupported"
          ? t("booking.successModal.share.unsupported")
          : "";

  const modal = (
    <>
      {/* Backdrop + centered card */}
      <div
        className="booking-success-backdrop"
        role="dialog"
        aria-modal="true"
        aria-labelledby="booking-success-title"
        onClick={onClose}
        style={{
          position: "fixed",
          inset: 0,
          background: BACKDROP_BG,
          zIndex: 1080,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "clamp(12px, 4vw, 32px)",
          overflowY: "auto",
        }}
      >
        <div
          className="booking-success-card"
          onClick={(e) => e.stopPropagation()}
          style={{
            position: "relative",
            background: palette.surface,
            border: palette.border,
            borderRadius: "clamp(16px, 3vw, 22px)",
            boxShadow: "0 30px 60px rgba(15, 23, 42, 0.25)",
            width: "100%",
            maxWidth: 560,
            padding: "clamp(20px, 5vw, 36px) clamp(18px, 5vw, 32px) clamp(18px, 4vw, 28px)",
            display: "grid",
            gap: "clamp(14px, 3vw, 20px)",
            maxHeight: "calc(100dvh - 24px)",
            overflowY: "auto",
            overscrollBehavior: "contain",
          }}
        >
          {/* Close X */}
          <button
            type="button"
            onClick={onClose}
            aria-label={t("booking.successModal.close")}
            style={{
              position: "absolute",
              top: 12,
              right: 12,
              width: 34,
              height: 34,
              borderRadius: "50%",
              border: palette.border,
              background: palette.surfaceAlt,
              color: palette.textMuted,
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.05rem",
              lineHeight: 1,
            }}
          >
            <i className="las la-times" aria-hidden="true" />
          </button>

          {/* Hero — checkmark + title */}
          <div className="booking-success-hero d-flex flex-column align-items-center text-center" style={{ gap: 12 }}>
            <span
              className="booking-success-check"
              style={{
                width: "clamp(64px, 16vw, 80px)",
                height: "clamp(64px, 16vw, 80px)",
                borderRadius: "50%",
                background: palette.accent,
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "clamp(2rem, 6vw, 2.6rem)",
                boxShadow: `0 12px 30px ${palette.accentSoft}`,
              }}
              aria-hidden="true"
            >
              <i className="las la-check" />
            </span>
            <h3
              id="booking-success-title"
              className="fw-700 mb-0"
              style={{
                color: palette.accent,
                fontSize: "clamp(1.05rem, 3vw, 1.3rem)",
                lineHeight: 1.3,
              }}
            >
              {t("booking.successModal.title")}
            </h3>
          </div>

          {/* Info card */}
          <div
            className="booking-success-info"
            style={{
              background: palette.surfaceAlt,
              border: palette.border,
              borderRadius: 16,
              overflow: "hidden",
            }}
          >
            {/* Branch row */}
            <div
              className="d-flex align-items-start"
              style={{
                padding: "clamp(12px, 3vw, 16px) clamp(14px, 3.5vw, 18px)",
                gap: 10,
                background: palette.surface,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  className="fw-700"
                  style={{
                    color: palette.text,
                    fontSize: "clamp(0.95rem, 2.5vw, 1.05rem)",
                    lineHeight: 1.3,
                    wordBreak: "break-word",
                  }}
                >
                  {branchName || orgName || "—"}
                </div>
                {orgName && orgName !== branchName ? (
                  <div
                    style={{
                      color: palette.textMuted,
                      fontSize: "0.85rem",
                      marginTop: 2,
                      wordBreak: "break-word",
                    }}
                  >
                    {orgName}
                  </div>
                ) : serviceName ? (
                  <div
                    style={{
                      color: palette.textMuted,
                      fontSize: "0.85rem",
                      marginTop: 2,
                      wordBreak: "break-word",
                    }}
                  >
                    {serviceName}
                  </div>
                ) : null}
              </div>
              <span
                style={{
                  width: 38,
                  height: 38,
                  borderRadius: 12,
                  background: palette.accentSoft,
                  color: palette.accent,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "1.2rem",
                  flexShrink: 0,
                }}
                aria-hidden="true"
              >
                <i className="las la-hospital-symbol" />
              </span>
            </div>

            {/* Pills */}
            <div
              className="booking-success-summary-grid"
              style={{
                padding: "clamp(10px, 2.5vw, 14px) clamp(14px, 3.5vw, 18px) clamp(8px, 2vw, 12px)",
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(90px, 1fr))",
                gap: 8,
              }}
            >
              {[
                {
                  label: t("booking.successModal.summary.date"),
                  value: dateLabel,
                  icon: "las la-calendar",
                },
                {
                  label: t("booking.successModal.summary.time"),
                  value: timeLabel,
                  icon: "las la-clock",
                },
                {
                  label: t("booking.successModal.summary.address"),
                  value: address,
                  icon: "las la-map-marker-alt",
                },
              ]
                .filter((row) => row.value)
                .map((row) => (
                  <div
                    key={row.label}
                    style={{
                      background: palette.surface,
                      border: palette.border,
                      borderRadius: 12,
                      padding: "8px 10px",
                      minWidth: 0,
                    }}
                  >
                    <div
                      style={{
                        fontSize: "0.7rem",
                        color: palette.textMuted,
                        textTransform: "uppercase",
                        letterSpacing: "0.04em",
                      }}
                    >
                      <i
                        className={`${row.icon} me-1`}
                        style={{ color: palette.accent, fontSize: "0.85rem" }}
                        aria-hidden="true"
                      />
                      {row.label}
                    </div>
                    <div
                      className="fw-700"
                      style={{
                        color: palette.text,
                        fontSize: "0.92rem",
                        marginTop: 2,
                        wordBreak: "break-word",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      {row.value}
                    </div>
                  </div>
                ))}
            </div>

            {address && mapUrl ? (
              <div
                style={{
                  padding: "0 clamp(14px, 3.5vw, 18px) clamp(8px, 2vw, 12px)",
                }}
              >
                <a
                  href={mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="d-flex align-items-center justify-content-between gap-3"
                  style={{
                    background: palette.accentSoft,
                    border: `1px solid ${palette.accentBorder}`,
                    borderRadius: 14,
                    color: palette.text,
                    padding: "10px 12px",
                    textDecoration: "none",
                  }}
                >
                  <span className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <span
                      style={{
                        width: 34,
                        height: 34,
                        borderRadius: 11,
                        background: palette.surface,
                        color: palette.accent,
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                      aria-hidden="true"
                    >
                      <i className="las la-map-marked-alt" />
                    </span>
                    <span style={{ minWidth: 0 }}>
                      <span className="fw-700 d-block" style={{ color: palette.text }}>
                        {t("booking.successModal.summary.map")}
                      </span>
                      <span
                        className="d-block"
                        style={{
                          color: palette.textMuted,
                          fontSize: "0.82rem",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {address}
                      </span>
                    </span>
                  </span>
                  <i
                    className="las la-external-link-alt"
                    aria-hidden="true"
                    style={{ color: palette.accent, flexShrink: 0 }}
                  />
                </a>
              </div>
            ) : null}

            {/* Code */}
            <div
              style={{
                padding: "clamp(8px, 2vw, 12px) clamp(14px, 3.5vw, 18px) clamp(14px, 3vw, 18px)",
              }}
            >
              <div
                style={{
                  fontSize: "0.75rem",
                  color: palette.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                {t("booking.successModal.codeLabel")}
              </div>
              <div
                style={{
                  color: palette.text,
                  fontFamily: BOOKING_FONT_FAMILY,
                  fontWeight: 700,
                  fontSize: "clamp(2.2rem, 8vw, 3.2rem)",
                  lineHeight: 1.05,
                  letterSpacing: "0.06em",
                  fontVariantNumeric: "tabular-nums",
                  marginTop: 4,
                }}
              >
                {code || "—"}
              </div>
            </div>
          </div>

          {/* Share via — custom branded chips. We avoid the Web Share API
              here because the OS-rendered sheet looks foreign next to the
              rest of our UI and is unavailable on most desktops. */}
          <div>
            <div
              className="booking-success-share-head d-flex align-items-center justify-content-between"
              style={{ marginBottom: 10, gap: 12, flexWrap: "wrap" }}
            >
              <div
                style={{
                  fontSize: "0.78rem",
                  color: palette.textMuted,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  fontWeight: 600,
                }}
              >
                {t("booking.successModal.share.via")}
              </div>
              {shareFeedback ? (
                <span
                  style={{
                    fontSize: "0.78rem",
                    color: palette.accent,
                    background: palette.accentSoft,
                    padding: "3px 10px",
                    borderRadius: 999,
                    fontWeight: 600,
                  }}
                >
                  <i className="las la-check me-1" aria-hidden="true" />
                  {shareFeedback}
                </span>
              ) : null}
            </div>
            <div
              className="booking-success-share-grid"
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(58px, 1fr))",
                gap: "clamp(4px, 1.5vw, 10px)",
                justifyItems: "center",
              }}
            >
              {shareTargets.map((target) => (
                <ShareChip
                  key={target.id}
                  target={target}
                  palette={palette}
                  onClick={() => handleShareTarget(target)}
                />
              ))}
            </div>
          </div>

          {/* Primary CTA — full width, View tickets (auth) or New booking */}
          {!isAuthenticated && typeof onRegister === "function" ? (
            <div
              className="booking-success-account"
              style={{
                background: palette.accentSoft,
                border: `1px solid ${palette.accentBorder}`,
                borderRadius: 16,
                padding: "12px 14px",
                display: "grid",
                gap: 10,
              }}
            >
              <div>
                <div className="fw-700" style={{ color: palette.text, fontSize: "0.98rem" }}>
                  {t("booking.accountPrompt.title")}
                </div>
                <div style={{ color: palette.textMuted, fontSize: "0.84rem", marginTop: 2 }}>
                  {t("booking.accountPrompt.text")}
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  onClose?.();
                  onRegister();
                }}
                className="pill fw-600"
                style={{
                  background: "transparent",
                  border: `1px solid ${palette.accentBorder}`,
                  color: palette.accent,
                  padding: "9px 14px",
                  width: "100%",
                }}
              >
                <i className="las la-user-plus me-1" aria-hidden="true" />
                {t("booking.accountPrompt.button")}
              </button>
            </div>
          ) : null}

          {isAuthenticated && ticketsHref ? (
            <Link
              to={ticketsHref}
              className="pill fw-600 d-inline-flex align-items-center justify-content-center"
              style={{
                background: palette.accent,
                border: "none",
                padding: "12px 16px",
                textDecoration: "none",
                fontSize: "0.95rem",
                color: "#fff",
                width: "100%",
              }}
              onClick={() => onClose?.()}
            >
              <i className="las la-ticket-alt me-2" aria-hidden="true" />
              {t("booking.successModal.actions.viewTickets")}
            </Link>
          ) : (
            <button
              type="button"
              onClick={() => {
                onClose?.();
                if (typeof onNewBooking === "function") onNewBooking();
              }}
              className="pill fw-600"
              style={{
                background: palette.accent,
                color: "#fff",
                border: "none",
                padding: "12px 16px",
                cursor: "pointer",
                fontSize: "0.95rem",
                width: "100%",
              }}
            >
              <i className="las la-plus me-2" aria-hidden="true" />
              {t("booking.successModal.actions.newBooking")}
            </button>
          )}
        </div>
      </div>
      <style>
        {`
          .booking-success-card {
            scrollbar-width: thin;
            scrollbar-color: rgba(45, 212, 191, 0.5) transparent;
          }

          .booking-success-card::-webkit-scrollbar {
            width: 6px;
          }

          .booking-success-card::-webkit-scrollbar-track {
            background: transparent;
          }

          .booking-success-card::-webkit-scrollbar-thumb {
            background: rgba(45, 212, 191, 0.45);
            border-radius: 999px;
          }

          @media (max-width: 480px) {
            .booking-success-backdrop {
              align-items: flex-start !important;
              padding: 8px !important;
            }

            .booking-success-card {
              max-height: calc(100dvh - 16px) !important;
              border-radius: 18px !important;
              padding: 14px 14px 16px !important;
              gap: 10px !important;
            }

            .booking-success-hero {
              gap: 8px !important;
              padding: 0 28px !important;
            }

            .booking-success-check {
              width: 52px !important;
              height: 52px !important;
              font-size: 1.8rem !important;
            }

            #booking-success-title {
              font-size: 1rem !important;
              line-height: 1.25 !important;
            }

            .booking-success-summary-grid {
              grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
              gap: 7px !important;
            }

            .booking-success-summary-grid > div {
              padding: 8px !important;
            }

            .booking-success-share-head {
              margin-bottom: 6px !important;
            }

            .booking-success-share-grid {
              gap: 4px !important;
            }

            .booking-success-account {
              padding: 10px 12px !important;
              gap: 8px !important;
            }
          }
        `}
      </style>
    </>
  );

  return createPortal(modal, document.body);
}

export default TicketSuccessModal;
